import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'src/client/redux/modules/helpers/useSelector';
import Helmet from 'react-helmet';
import { useParams, useHistory } from 'react-router';
import { useIntl } from 'react-intl';
import { Tab, Tabs, ContentHeader } from '@tovia/man-ui';
import { Link } from 'react-router-dom';
import camelCase from 'lodash/camelCase';
import LoaderWrapper from 'src/client/components/Loader/LoaderWrapper';
import { load as loadLeaderboard, defaultTab, defaultTimePeriod } from 'src/client/redux/modules/leaderboard';
import { ContentListSection } from '../ContentListSection/ContentListSection';

const linkPrefix = 'leaderboard';
const isOffCenter = true;

const sortOptions = {
  day: 'Yesterday',
  week: 'Last Week',
  month: 'This Month',
  year: 'This Year',
};

const defaultLeaderboardTabs = [
  {
    value: 'galleries',
    name: 'Galleries',
    to: 'galleries',
  },
  {
    value: 'movies',
    name: 'Movies',
    to: 'movies',
  },
  {
    value: 'models',
    name: 'Models',
    to: 'models',
  },
];

export default function Leaderboard() {
  const { tab = defaultTab, timePeriod } = useParams<{ tab: string; timePeriod: string }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const config = useSelector((state: any) => state.app.config);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const site = useSelector((state: any) => state.site);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { items, loading, lastCallParams, error } = useSelector((state: any) => state.leaderboard);
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();

  const tabs = config.leaderboardTabs || defaultLeaderboardTabs;
  const viewPeriod = timePeriod || defaultTimePeriod;

  const loadData = useCallback(() => {
    const params = {
      tab,
      order: viewPeriod,
    };
    dispatch(loadLeaderboard(params));
  }, [dispatch, tab, viewPeriod]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const changeTimePeriod = (value) => {
    history.push(`/${linkPrefix}/${tab}/${value}`);
  };

  const translateTabName = (name) =>
    intl.formatMessage({ id: `leaderboard.tabs.${name.toLowerCase()}`, defaultMessage: name });

  const tabsNode = (
    <Tabs>
      {tabs.map((cTab) => (
        <Tab
          {...cTab}
          key={cTab.value}
          as={Link}
          to={`/${linkPrefix}/${cTab.to || cTab.link}`}
          active={cTab.value === tab}
          name={translateTabName(cTab.name || cTab.label)}
        />
      ))}
    </Tabs>
  );

  const capFirst = (str) => (str && `${str.charAt(0).toUpperCase()}${str.substr(1)}`) || '';
  const translatedTitle = {
    title: intl.formatMessage({
      id: `leaderboard.tabs.topViewed.${camelCase(tab)}`,
      defaultMessage: `Top Viewed ${tab === 'photographers' ? 'Artists' : capFirst(tab)}`,
    }),
    sort: intl.formatMessage({
      id: `leaderboard.sortOptions.${camelCase(sortOptions[viewPeriod])}`,
      defaultMessage: sortOptions[viewPeriod],
    }),
  };
  const generateHeaderTitle = ({ title, sort }) => `${title}: ${sort}`;

  let contentListType;

  switch (lastCallParams.tab) {
    case 'photographers':
      contentListType = 'PHOTOGRAPHER';
      break;
    case 'models':
      contentListType = 'MODEL';
      break;
    default:
      contentListType = 'GALLERY';
      break;
  }

  const { siteSubdomain } = useSelector((state) => state.app);
  const url = `https://${siteSubdomain}.${site.domainCanonical}/leaderboard/models`;

  return (
    <div className="content container">
      <Helmet>
        <title>{`${site.name} - Leaderboard`}</title>
        <meta property="og:title" content={`${site.name} - Leaderboard`} />
        <meta property="og:url" content={url} />
        <link rel="canonical" href={url} />
      </Helmet>
      <ContentHeader title={generateHeaderTitle(translatedTitle)} tabs={tabsNode} />
      {loading || error ? (
        <LoaderWrapper
          defaultHeight
          reloadFunction={loadData}
          loadingStatus={loading}
          reduxErrorStatus={error}
          contentType="leaderboard"
        />
      ) : (
        <div>
          <ContentListSection
            items={items}
            sortDropdownProps={{
              menuOptions: [
                {
                  id: 'day',
                  title: intl.formatMessage({ id: 'leaderboard.sortOptions.yesterday', defaultMessage: 'Yesterday' }),
                  intlID: 'leaderboard.sortOptions.yesterday',
                },
                {
                  id: 'week',
                  title: intl.formatMessage({ id: 'leaderboard.sortOptions.lastWeek', defaultMessage: 'Last Week' }),
                  intlID: 'leaderboard.sortOptions.lastWeek',
                },
                {
                  id: 'month',
                  title: intl.formatMessage({
                    id: 'leaderboard.sortOptions.thisMonth',
                    defaultMessage: 'This Month',
                  }),
                  intlID: 'leaderboard.sortOptions.thisMonth',
                },
                {
                  id: 'year',
                  title: intl.formatMessage({ id: 'leaderboard.sortOptions.thisYear', defaultMessage: 'This Year' }),
                  intlID: 'leaderboard.sortOptions.thisYear',
                },
              ],
              onChange: changeTimePeriod,
              value: viewPeriod,
              title: intl.formatMessage({ id: 'leaderboard.timePeriod', defaultMessage: 'Time Period' }),
              isOffCenter,
            }}
            type={contentListType}
            viewPeriod={viewPeriod}
          />
        </div>
      )}
    </div>
  );
}
